/* VESSA: one offer state, native galleries and accessible purchase forms. */
(() => {
  if (customElements.get('vessa-pdp')) return;
  class VessaPdp extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controller = new AbortController();
      this.observers = [];
      this.cleanups = [];
      this.product = JSON.parse(this.querySelector('[data-product-data]').textContent);
      this.forms = [...this.querySelectorAll('.v-offer-form')];
      this.variant = this.product.variants.find(v => String(v.id) === this.querySelector('[data-variant-id]').value) || this.product.variants[0];
      this.quantity = 1;
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.dataset.currency || 'USD' });
      this.listen(this, 'change', e => {
        if (e.target.matches('input[type=radio][name=quantity]')) {
          this.quantity = Number(e.target.value);
          this.updateOffer(true);
          this.track('offer_selected', { days: this.quantity * 30, quantity: this.quantity, source: e.target.form.dataset.source });
        }
        if (e.target.matches('[data-variant-select]')) {
          this.variant = this.product.variants.find(v => String(v.id) === e.target.value);
          this.updateOffer(true);
          const url = new URL(location.href);
          url.searchParams.set('variant', this.variant.id);
          history.replaceState({}, '', url);
        }
      });
      this.forms.forEach(form => this.listen(form, 'submit', e => this.addToCart(e)));
      this.querySelectorAll('[data-slider]').forEach(track => this.setupSlider(track));
      this.querySelectorAll('details[data-event]').forEach(details => {
        this.listen(details, 'toggle', () => {
          const summary = details.querySelector(':scope > summary');
          summary.setAttribute('aria-expanded', String(details.open));
          if (details.open) this.track(details.dataset.event, { title: summary.textContent.trim() });
        });
        const summary = details.querySelector(':scope > summary');
        const content = details.querySelector(':scope > div');
        summary.setAttribute('aria-expanded', String(details.open));
        if (content) {
          content.id = 'v-panel-' + this.dataset.sectionId + '-' + [...this.querySelectorAll('details')].indexOf(details);
          summary.setAttribute('aria-controls', content.id);
          if (details.classList.contains('v-ingredient')) this.setupIngredientMotion(details, summary);
        }
      });
      this.setupZoom();
      this.updateOffer();
      this.setupSticky();
      this.setupScrollEvents();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observers?.forEach(observer => observer.disconnect());
      this.cleanups?.forEach(cleanup => cleanup());
      this.initialized = false;
    }
    listen(target, name, callback, options = {}) {
      target.addEventListener(name, callback, { ...options, signal: this.controller.signal });
    }
    setupIngredientMotion(details, summary) {
      let animation = null;
      let targetOpen = details.open;
      const clear = () => {
        animation?.cancel();
        animation = null;
        details.style.overflow = '';
      };
      this.cleanups.push(clear);
      this.listen(summary, 'click', event => {
        event.preventDefault();
        targetOpen = !(animation ? targetOpen : details.open);
        const startHeight = details.getBoundingClientRect().height;
        clear();
        if (matchMedia('(prefers-reduced-motion: reduce)').matches || !details.animate) {
          details.open = targetOpen;
          return;
        }
        if (targetOpen) details.open = true;
        const style = getComputedStyle(details);
        const border = parseFloat(style.borderTopWidth) + parseFloat(style.borderBottomWidth);
        const endHeight = targetOpen ? details.getBoundingClientRect().height : summary.getBoundingClientRect().height + border;
        details.style.overflow = 'hidden';
        const current = details.animate(
          [{ height: startHeight + 'px' }, { height: endHeight + 'px' }],
          { duration: 260, easing: 'cubic-bezier(.2,.7,.3,1)' }
        );
        animation = current;
        current.onfinish = () => {
          if (animation !== current) return;
          details.open = targetOpen;
          clear();
        };
      });
    }
    track(event, data = {}) {
      const detail = { event, product_id: this.product.id, ...data };
      this.dispatchEvent(new CustomEvent('vessa:analytics', { bubbles: true, detail }));
      // Connect this dataLayer to the store's analytics container if desired.
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(detail);
    }
    validQuantity(quantity) {
      const rule = this.variant.quantity_rule || { min: 1, max: null, increment: 1 };
      return quantity >= rule.min && (!rule.max || quantity <= rule.max) && quantity % rule.increment === 0;
    }
    unitPrice(quantity) {
      let price = this.variant.price;
      for (const tier of this.variant.quantity_price_breaks || []) {
        if (quantity >= tier.minimum_quantity) price = tier.price;
      }
      return price;
    }
    savings(quantity) { return ({ 3: 2098, 5: 5496 })[quantity] || 0; }
    total(quantity) { return this.unitPrice(quantity) * quantity - this.savings(quantity); }
    format(cents) { return this.money.format(cents / 100); }
    updateOffer(announce = false) {
      if (!this.validQuantity(this.quantity)) this.quantity = [1, 3, 5].find(q => this.validQuantity(q)) || 1;
      const available = this.variant.available && this.validQuantity(this.quantity);
      const price = this.format(this.total(this.quantity));
      this.forms.forEach(form => {
        form.querySelector('[data-variant-id]').value = this.variant.id;
        form.querySelectorAll('[name=quantity]').forEach(input => {
          if (input.type === 'radio') {
            input.checked = Number(input.value) === this.quantity;
            input.disabled = !this.validQuantity(Number(input.value));
          } else input.value = this.quantity;
        });
        const label = form.querySelector('[data-cta-label]');
        label.textContent = available ? 'Add ' + this.quantity + ' Bottle' + (this.quantity === 1 ? '' : 's') + ' — ' + price : (this.variant.available ? 'Unavailable quantity' : 'Sold out');
        form.querySelector('[data-add-to-cart]').disabled = !available || this.busy;
        form.querySelectorAll('[data-quantity-price]').forEach(el => { el.textContent = this.format(this.total(Number(el.dataset.quantityPrice))); });
        form.querySelectorAll('[data-quantity-original]').forEach(el => { el.textContent = this.format(this.variant.price * Number(el.dataset.quantityOriginal)); });
        form.querySelectorAll('[data-quantity-unit]').forEach(el => { el.textContent = this.format(this.total(Number(el.dataset.quantityUnit)) / Number(el.dataset.quantityUnit)) + ' / bottle'; });
      });
      this.querySelectorAll('[data-offer-summary]').forEach(summary => { summary.textContent = this.quantity * 30 + '-day supply · ' + price; });
      if (announce) this.querySelector('[data-status]').textContent = this.quantity * 30 + '-day supply selected. ' + price;
    }
    async addToCart(event) {
      event.preventDefault();
      if (this.busy || !this.variant.available || !this.validQuantity(this.quantity)) return;
      const form = event.currentTarget;
      const selected = { id: this.variant.id, quantity: this.quantity, price: this.total(this.quantity) };
      this.busy = true;
      this.forms.forEach(f => {
        f.querySelector('[data-add-to-cart]').disabled = true;
        f.querySelector('.v-form-error').hidden = true;
        f.querySelectorAll('fieldset').forEach(fieldset => { fieldset.disabled = true; });
      });
      const select = this.querySelector('[data-variant-select]');
      if (select) select.disabled = true;
      form.querySelector('[data-cta-label]').textContent = 'Adding…';
      try {
        const response = await fetch(this.dataset.addUrl + '.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: selected.id, quantity: selected.quantity }] })
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'We couldn’t add this supply. Please try a smaller quantity.');
        this.track('add_to_cart', {
          source: form.dataset.source, currency: this.dataset.currency, value: selected.price / 100,
          items: [{ item_id: String(selected.id), item_name: this.product.title, quantity: selected.quantity, price: this.total(selected.quantity) / selected.quantity / 100 }]
        });
        window.location.assign(this.dataset.cartUrl);
      } catch (error) {
        const message = error instanceof TypeError ? 'We couldn’t confirm the cart update. Please check your cart before trying again.' : error.message;
        form.querySelector('.v-form-error').textContent = message;
        form.querySelector('.v-form-error').hidden = false;
        this.querySelector('[data-status]').textContent = message;
        this.busy = false;
        this.forms.forEach(f => f.querySelectorAll('fieldset').forEach(fieldset => { fieldset.disabled = false; }));
        if (select) select.disabled = false;
        this.updateOffer();
      }
    }
    setupSlider(track) {
      const nav = this.querySelector('[data-dots="' + track.dataset.slider + '"]');
      const dots = [...nav.querySelectorAll('button')];
      const slides = [...track.children];
      let active = 0;
      const go = index => {
        index = Math.max(0, Math.min(index, slides.length - 1));
        const padding = parseFloat(getComputedStyle(track).paddingLeft);
        const left = slides[index].getBoundingClientRect().left - track.getBoundingClientRect().left + track.scrollLeft - padding;
        track.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      };
      dots.forEach((dot, index) => this.listen(dot, 'click', () => go(index)));
      this.listen(track, 'keydown', e => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') { e.preventDefault(); go(active + (e.key === 'ArrowRight' ? 1 : -1)); }
      });
      const observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting || entry.intersectionRatio < .6) continue;
          const index = slides.indexOf(entry.target);
          dots.forEach((dot, i) => i === index ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current'));
          if (track.dataset.slider === 'gallery') {
            const thumb = dots[index];
            const thumbCenter = thumb.offsetLeft + thumb.offsetWidth / 2;
            nav.scrollTo({ left: thumbCenter - nav.clientWidth / 2, behavior: 'smooth' });
          }
          if (index !== active) this.track(track.dataset.slider === 'gallery' ? 'gallery_slide_view' : 'recognition_card_swipe', { slide: index + 1 });
          active = index;
        }
      }, { root: track, threshold: .6 });
      slides.forEach(slide => observer.observe(slide));
      this.observers.push(observer);
    }
    setupSticky() {
      const sticky = this.querySelector('[data-sticky]');
      const buttons = [...this.querySelectorAll('[data-add-to-cart], [data-purchase-cta]')];
      const purchaseSection = this.querySelector('.v-hero');
      let frame;
      const refresh = () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const visible = el => {
            const rect = el.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0 && rect.top < innerHeight && rect.bottom > 0;
          };
          const passedPurchaseSection = purchaseSection && purchaseSection.getBoundingClientRect().bottom <= 0;
          const shouldShow = passedPurchaseSection && !buttons.some(visible) && !this.querySelector('dialog[open]');
          if (sticky.hidden && shouldShow) this.track('sticky_cta_impression');
          sticky.hidden = !shouldShow;
        });
      };
      const observer = new IntersectionObserver(refresh, { threshold: [0, 1] });
      buttons.forEach(el => observer.observe(el));
      const resizeObserver = new ResizeObserver(refresh);
      resizeObserver.observe(this);
      this.observers.push(resizeObserver);
      const dialog = this.querySelector('dialog');
      const dialogObserver = new MutationObserver(refresh);
      dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
      this.observers.push(dialogObserver);
      this.observers.push(observer);
      this.listen(window, 'scroll', refresh, { passive: true });
      this.listen(window, 'resize', refresh);
      this.cleanups.push(() => cancelAnimationFrame(frame));
      refresh();
    }
    setupZoom() {
      const dialog = this.querySelector('dialog');
      this.querySelectorAll('[data-zoom]').forEach(button => this.listen(button, 'click', () => {
        const source = button.querySelector('img');
        const image = dialog.querySelector('img');
        const url = new URL(source.src);
        url.searchParams.set('width', '1600');
        image.src = url.href;
        image.alt = source.alt;
        dialog.showModal();
      }));
      this.listen(dialog.querySelector('[data-close-zoom]'), 'click', () => dialog.close());
      this.listen(dialog, 'click', e => { if (e.target === dialog) dialog.close(); });
    }
    setupScrollEvents() {
      const seen = new Set();
      this.listen(window, 'scroll', () => {
        const distance = document.documentElement.scrollHeight - innerHeight;
        const progress = distance > 0 ? scrollY / distance * 100 : 100;
        [25, 50, 75, 100].forEach(depth => {
          if (progress >= depth - 1 && !seen.has(depth)) { seen.add(depth); this.track('scroll_depth', { percent: depth }); }
        });
      }, { passive: true });
    }
  }
  customElements.define('vessa-pdp', VessaPdp);
})();
